/**
 * ImportSignatureModal — pulls the user's current Gmail signature (via the
 * fetchUserSignature callable + DWD), reconstructs it as an editable spec
 * with importSignatureHtml, and shows the original and the reconstruction
 * side by side so the user can verify they match before importing.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  UnorderedList,
  ListItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiDownloadCloud } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { importSignatureHtml } from './importSignatureHtml';
import { compileSignatureHtml } from './compileSignatureHtml';

export default function ImportSignatureModal({ isOpen, onClose, onImport }) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [state, setState] = useState({ status: 'idle', originalHtml: '', spec: null, warnings: [], error: null });
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setState({ status: 'loading', originalHtml: '', spec: null, warnings: [], error: null });
    (async () => {
      try {
        const functions = getFunctions(app, 'us-east1');
        const fetchSignature = httpsCallable(functions, 'fetchUserSignature');
        const result = await fetchSignature({});
        const html = result.data?.signature || '';
        if (cancelled) return;
        if (!html.trim()) {
          setState({ status: 'empty', originalHtml: '', spec: null, warnings: [], error: null });
          return;
        }
        const imported = await importSignatureHtml(html);
        if (cancelled) return;
        if (!imported.ok) {
          setState({ status: 'error', originalHtml: html, spec: null, warnings: imported.warnings || [], error: imported.error });
          return;
        }
        setState({ status: 'ready', originalHtml: html, spec: imported.spec, warnings: imported.warnings, error: null });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', originalHtml: '', spec: null, warnings: [], error: err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const reconstructedHtml = useMemo(
    () => (state.spec ? compileSignatureHtml(state.spec) : ''),
    [state.spec]
  );

  const handleImport = useCallback(() => {
    if (!state.spec) return;
    setImporting(true);
    try {
      onImport(state.spec, state.warnings);
      onClose();
    } finally {
      setImporting(false);
    }
  }, [state.spec, state.warnings, onImport, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import your current Gmail signature</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          {state.status === 'loading' && (
            <Flex align="center" justify="center" minH="160px" direction="column">
              <Spinner color="purple.500" mb={3} />
              <Text fontSize="sm" color={mutedColor}>Reading your Gmail signature…</Text>
            </Flex>
          )}

          {state.status === 'empty' && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              You don't have a signature set in Gmail yet — build one here from a template instead.
            </Alert>
          )}

          {state.status === 'error' && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Couldn't import your signature: {state.error}
            </Alert>
          )}

          {state.status === 'ready' && (
            <>
              <Text fontSize="sm" color={mutedColor} mb={3}>
                Check that both sides look the same. Importing creates a <b>new</b> signature in the editor —
                nothing in your Gmail changes until you install it again.
              </Text>
              <Flex direction={{ base: 'column', lg: 'row' }} style={{ gap: '12px' }}>
                <Box flex={1} minW={0}>
                  <Text fontSize="xs" fontWeight="bold" mb={1}>Currently in Gmail</Text>
                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3} bg="white" overflowX="auto">
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: state.originalHtml }} />
                  </Box>
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontSize="xs" fontWeight="bold" mb={1}>After import (editable)</Text>
                  <Box borderWidth="1px" borderColor="purple.300" borderRadius="md" p={3} bg="white" overflowX="auto">
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: reconstructedHtml }} />
                  </Box>
                </Box>
              </Flex>

              {state.warnings.length > 0 && (
                <Alert status="warning" borderRadius="md" mt={3} alignItems="flex-start">
                  <AlertIcon mt={0.5} />
                  <Box fontSize="xs">
                    <Text fontWeight="bold" mb={1}>A few things couldn't be copied 1:1 — double-check them:</Text>
                    <UnorderedList spacing={0.5} pl={1}>
                      {state.warnings.map((w) => <ListItem key={w}>{w}</ListItem>)}
                    </UnorderedList>
                  </Box>
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              colorScheme="purple"
              leftIcon={<FiDownloadCloud />}
              onClick={handleImport}
              isLoading={importing}
              isDisabled={state.status !== 'ready'}
            >
              Import as new signature
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
